import React from 'react';
import { motion } from 'framer-motion';
import { MousePointer2, ShoppingCart, Search, Mail, Code, Layout, PenTool, BookOpen,  Globe, GlassWater, Wand2, Palette, Lightbulb, Megaphone} from 'lucide-react';
import { HashLink } from 'react-router-hash-link';



const Intro = () => {

  // Ajustement des positions en unités relatives (vw/vh) pour la réactivité et le rendu immédiat
  const services = [
    { icon: <Layout />, color: "bg-blue-500", label: "Webdesign", x: "-12vw", y: "-10vh" },
    { icon: <Code />, color: "bg-purple-500", label: "Dev React", x: "12vw", y: "-8vh" },
    { icon: <Search />, color: "bg-green-500", label: "SEO", x: "15vw", y: "10vh" },
    { icon: <ShoppingCart />, color: "bg-orange-500", label: "E-commerce", x: "-13vw", y: "8vh" },
    { icon: <Mail />, color: "bg-red-500", label: "Marketing", x: "0vw", y: "-18vh" },
    { icon: <PenTool />, color: "bg-pink-500", label: "Copywriting", x: "-20vw", y: "-3vh" }, 
    { icon: <BookOpen />, color: "bg-yellow-600", label: "Content", x: "10vw", y: "-20vh" },
    { icon: <Globe />, color: "bg-blue-700", label: "WordPress", x: "-8vw", y: "18vh" },
    { icon: <GlassWater />, color: "bg-teal-500", label: "Research", x: "-22vw", y: "-12vh" },
    { icon: <Wand2 />, color: "bg-indigo-400", label: "Creativity", x: "6vw", y: "20vh" },
    { icon: <Megaphone />, color: "bg-orange-600", label: "Ads", x: "22vw", y: "-2vh" },
    { icon: <Palette />, color: "bg-cyan-500", label: "Logo", x: "18vw", y: "18vh" },
    { icon: <Lightbulb />, color: "bg-yellow-400", label: "Idea", x: "-18vw", y: "18vh" },
  ];

  const logo = "favicon.png";

  return (
    // pb-32 ou pb-40 pour laisser de la place à votre barre de navigation basse
    <section className="relative grid grid-cols-1 lg:grid-cols-2 min-h-screen items-center gap-8 px-6 md:px-12 py-10 pb-24 bg-white overflow-hidden">


     {/* --- LOGO + BANDEAU DÉFILANT PROFESSIONNEL --- */}
      <div className="absolute top-6 left-0 w-full px-6 md:px-12 z-50 flex items-center gap-4 md:gap-8">
        
        {/* Le Logo avec son animation existante */}
        <motion.div 
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1, ease: "backOut" }}
          className="flex-shrink-0"
        >
          <div className="relative group">
            <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full scale-150 animate-pulse"></div>
            <motion.img 
              src={logo} 
              alt="Mesmin Vision Logo" 
              className="h-10 md:h-14 w-auto relative z-10 drop-shadow-xl"
              whileHover={{ scale: 1.05 }}
            />
          </div>
        </motion.div>

        {/* Le Bandeau Défilant (Marquee) */}
        <div className="relative flex-1 overflow-hidden">
          {/* Effet de fondu sur les bords pour le côté "Doux" */}
          <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-r from-white via-transparent to-white"></div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex whitespace-nowrap"
          >
            <motion.p 
              animate={{ x: [0, -1000] }}
              transition={{ 
                x: { duration: 25, repeat: Infinity, ease: "linear" } 
              }}
              className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-slate-500/80 flex items-center gap-8"
            >
              <span>Sichern Sie sich jetzt Ihr unverbindliches Beratungsgespräch</span>
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              <span>Sichern Sie sich jetzt Ihr unverbindliches Beratungsgespräch</span>
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              <span>Sichern Sie sich jetzt Ihr unverbindliches Beratungsgespräch</span>
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              {/* On répète pour que le défilement soit continu sans coupure */}
              <span>Sichern Sie sich jetzt Ihr unverbindliches Beratungsgespräch</span>
            </motion.p>
          </motion.div>
        </div>
      </div>
      {/* ----------------------------------------------- */}

      
      {/* SECTION GAUCHE */}
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="z-20 text-center lg:text-left order-2 lg:order-1"
      >
        <h1 className="text-3xl md:text-3xl lg:text-5xl font-extrabold text-secondary leading-tight">
         Digitale Zukunft <span className="text-primary">gestalten</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0">
          Webdesign, das <strong>Eindruck</strong> macht und <strong>Ergebnisse</strong> liefert.
        </p>
        <HashLink smooth to="/kontakte#contact-form">
          <button className="mt-8 px-8 py-4 bg-primary text-white rounded-lg font-bold shadow-lg hover:scale-105 hover:bg-secondary transition-all active:scale-95">
            Projekt anfragen
          </button>
        </HashLink>
        
      </motion.div>

      {/* SECTION DROITE : ANIMATION */}
      <div className="relative flex justify-center items-center h-[300px] md:h-[500px] order-1 lg:order-2 scale-75 md:scale-100">
        <motion.div 
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="z-10 w-32 h-56 md:w-48 md:h-80 bg-slate-100 rounded-[2.5rem] md:rounded-[3rem] border-4 md:border-8 border-slate-900 shadow-2xl relative overflow-hidden flex items-center justify-center"
        >
          <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-100 flex items-center justify-center">
             <MousePointer2 size={40} className="text-primary animate-pulse" />
          </div>
        </motion.div>

        {services.map((service, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              x: service.x,
              y: [service.y, `calc(${service.y} - 15px)`, service.y] 
            }}
            transition={{ 
              delay: index * 0.05, // Délai réduit pour un chargement plus rapide
              y: { duration: 3 + index, repeat: Infinity, ease: "easeInOut" } 
            }}
            className={`absolute p-2 md:p-4 rounded-xl md:rounded-2xl text-white shadow-lg ${service.color} z-20`}
          >
            <div className="scale-75 md:scale-100">{service.icon}</div>
            <span className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 text-slate-800 text-[10px] font-bold whitespace-nowrap bg-white/80 px-2 rounded">
              {service.label}
            </span>
          </motion.div>
        ))}

        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute w-[280px] h-[280px] md:w-[450px] md:h-[450px] border-2 border-dashed border-slate-200 rounded-full"
        />
      </div>
    </section>
  );
  
}

export default Intro;